import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getAuthorArticle, getAuthorProfile } from '../services/authorApi';
import { normalizeMarkdownContent } from '../utils/markdownContent';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatLocalDate } from '../utils/formatDate';
import '../styles/AuthorMarkdown.css';
import './AuthorArticlePage.css';

const markdownLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

function ArticleMeta({ author, article, isNewsAgent }) {
  return (
    <header className="author-article-header">
      {isNewsAgent && <p className="author-article-badge">NewsAgent Short</p>}
      <h1>{article.title}</h1>
      <p className="author-article-meta">
        {author.displayName}
        {article.publishedAt ? ` · ${formatLocalDate(article.publishedAt)}` : ''}
        {article.durationSeconds ? ` · ${article.durationSeconds}s` : ''}
      </p>
    </header>
  );
}

function ArticleSources({ article }) {
  if (!article.sourceTitles?.length) return null;
  return (
    <footer className="author-sources">
      <h3>Sources</h3>
      <ul>
        {article.sourceTitles.map((title, i) => (
          <li key={`${title}-${i}`}>
            {article.sourceUrls?.[i] ? (
              <a href={article.sourceUrls[i]} target="_blank" rel="noopener noreferrer">
                {title}
              </a>
            ) : (
              title
            )}
          </li>
        ))}
      </ul>
    </footer>
  );
}

const AuthorArticlePage = () => {
  const { slug, articleId } = useParams();
  const [author, setAuthor] = useState(null);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug || !articleId) return;
    setLoading(true);
    setError(null);
    Promise.all([getAuthorProfile(slug), getAuthorArticle(slug, articleId)])
      .then(([profile, detail]) => {
        setAuthor(profile);
        setArticle(detail);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, articleId]);

  useDocumentTitle(
    article?.title ? `${article.title} · ${author?.displayName || slug}` : 'Author article',
  );

  if (loading) {
    return (
      <div className="author-article-page">
        <div className="container">
          <p className="author-status">Loading article…</p>
        </div>
      </div>
    );
  }

  if (error || !article || !author) {
    return (
      <div className="author-article-page">
        <div className="container">
          <h1>Article not found</h1>
          <p className="author-error">{error || 'This article is unavailable.'}</p>
          <Link to={`/authors/${slug}`} className="btn btn-outline-primary">
            Back to channel
          </Link>
        </div>
      </div>
    );
  }

  const videoSrc = resolveMediaUrl(article.mediaUrl);
  const posterSrc = resolveMediaUrl(article.posterUrl);
  const isNewsAgent = article.articleType === 'newsagent_short';
  const hasVideo = Boolean(videoSrc);

  return (
    <div className={`author-article-page${hasVideo ? ' author-article-page--video' : ''}`}>
      <div className="container">
        <nav className="author-article-nav">
          <Link to={`/authors/${slug}`}>← {author.displayName}</Link>
        </nav>
        <article className="author-article-body">
          {hasVideo ? (
            <>
              <div className="author-article-split">
                <div className="author-article-video">
                  <video
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={posterSrc || undefined}
                    src={videoSrc}
                  >
                    <a href={videoSrc}>Download video</a>
                  </video>
                </div>
                <aside className="author-article-sidebar">
                  <ArticleMeta author={author} article={article} isNewsAgent={isNewsAgent} />
                  {article.content && (
                    <div className="author-markdown author-markdown--sidebar">
                      <ReactMarkdown components={{ a: markdownLink }}>
                        {normalizeMarkdownContent(article.content)}
                      </ReactMarkdown>
                    </div>
                  )}
                  <ArticleSources article={article} />
                </aside>
              </div>
            </>
          ) : (
            <>
              <ArticleMeta author={author} article={article} isNewsAgent={isNewsAgent} />
              {article.content && (
                <div className="author-markdown">
                  <ReactMarkdown components={{ a: markdownLink }}>
                    {normalizeMarkdownContent(article.content)}
                  </ReactMarkdown>
                </div>
              )}
              <ArticleSources article={article} />
            </>
          )}
        </article>
      </div>
    </div>
  );
};

export default AuthorArticlePage;
